import { TenantQueryProvider } from './tenant-query-provider';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { Navigate, NavLink, Outlet, useParams } from 'react-router';

import { FullPageState, PageLoadingState } from '@/components/common';
import { PageAccessProvider } from '@/components/providers/page-access-provider';
import { QuotaBlockProvider } from '@/components/providers/quota-block-provider';
import { Button } from '@/components/ui/button';
import type { AuthUser } from '@/hooks/auth-context';
import { useAuth } from '@/hooks/use-auth';
import { TenantCtx, type TenantContext } from '@/hooks/tenant-context';
import { apiClient } from '@/lib/api';

/**
 * The tenant query cache's scope: a new workspace or viewer starts a fresh cache, and demo
 * mode, which changes what the API returns, resets the existing one in place.
 */
function tenantQueryScope(tenantId: string, user: AuthUser | null): { readonly key: string; readonly demoMode: boolean } {
  return user === null
    ? { key: tenantId, demoMode: false }
    : { key: `${tenantId}:${user.user_id}`, demoMode: user.demo_mode };
}

function toTenantErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return 'The workspace could not be opened. Try again.';
}

function TenantLoadingView() {
  return (
    <div className="flex min-h-svh items-center justify-center bg-secondary px-6">
      <PageLoadingState variant="compact" label="Switching workspace…" />
    </div>
  );
}

function TenantErrorView({ message }: { readonly message: string }) {
  return (
    <FullPageState
      title="Workspace not found"
      message={message}
      tone="error"
      actions={
        <Button asChild variant="outline">
          <NavLink to="/">
            <ArrowLeftIcon data-icon="inline-start" />
            Back to home
          </NavLink>
        </Button>
      }
    />
  );
}

export function TenantProvider() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { availableMemberships, isLoading: isAuthLoading, switchTenant, user } = useAuth();
  const [switchError, setSwitchError] = useState<{ readonly message: string; readonly slug: string } | null>(
    null,
  );
  const switchAttemptedRef = useRef<string | null>(null);

  const slugMatchesSession = tenantSlug !== undefined && user?.tenant_slug === tenantSlug;
  const sessionReady = !isAuthLoading && tenantSlug !== undefined;
  const targetMembership =
    !sessionReady || slugMatchesSession
      ? null
      : availableMemberships.find(
          (membership) => membership.tenant_slug === tenantSlug && membership.is_active,
        ) ?? null;
  const activeError =
    tenantSlug !== undefined && switchError?.slug === tenantSlug ? switchError.message : null;
  const shouldAutoSwitch =
    sessionReady && !slugMatchesSession && targetMembership !== null;
  const shouldResolveSuperadminWorkspace =
    sessionReady &&
    !slugMatchesSession &&
    targetMembership === null &&
    user?.is_superadmin === true;

  useEffect(() => {
    if (!shouldAutoSwitch && !shouldResolveSuperadminWorkspace) {
      return;
    }

    if (switchAttemptedRef.current === tenantSlug) {
      return;
    }

    let isActive = true;
    switchAttemptedRef.current = tenantSlug;

    async function switchToRequestedWorkspace(): Promise<void> {
      if (targetMembership !== null) {
        await switchTenant(targetMembership.tenant_id);
        return;
      }

      const tenants = await apiClient.listTenants();
      const targetTenant = tenants.find((tenant) => tenant.slug === tenantSlug);
      if (targetTenant === undefined) {
        throw new Error(`Workspace "${tenantSlug}" was not found.`);
      }

      await switchTenant(targetTenant.tenant_id);
    }

    void switchToRequestedWorkspace()
      .catch((cause: unknown) => {
        if (!isActive) {
          return;
        }

        setSwitchError({
          message: toTenantErrorMessage(cause),
          slug: tenantSlug,
        });
      });

    return () => {
      isActive = false;
    };
  }, [
    shouldAutoSwitch,
    shouldResolveSuperadminWorkspace,
    switchTenant,
    targetMembership,
    tenantSlug,
  ]);

  const context = useMemo<TenantContext | null>(() => {
    if (!slugMatchesSession) {
      return null;
    }

    if (typeof user.tenant_display_name !== 'string' || typeof user.tenant_id !== 'string') {
      return null;
    }

    return {
      displayName: user.tenant_display_name,
      slug: tenantSlug,
      tenantId: user.tenant_id,
    };
  }, [slugMatchesSession, tenantSlug, user]);

  if (tenantSlug === undefined) {
    return <Navigate to="/" replace />;
  }

  if (isAuthLoading) {
    return <TenantLoadingView />;
  }

  if ((shouldAutoSwitch || shouldResolveSuperadminWorkspace) && activeError === null) {
    return <TenantLoadingView />;
  }

  if (context === null) {
    // Graceful fallback for mistyped or stale slugs (e.g. /dashboard, /home):
    // if the viewer has an active membership in some other workspace, send
    // them to that workspace instead of dead-ending on `Workspace not found`.
    // Superadmins skip this so they still see the explicit error when they
    // intentionally probe unknown slugs.
    if (activeError === null && user?.is_superadmin !== true) {
      const fallbackMembership =
        availableMemberships.find((membership) => membership.is_active) ?? null;
      if (fallbackMembership !== null && fallbackMembership.tenant_slug !== tenantSlug) {
        return <Navigate to={`/${fallbackMembership.tenant_slug}`} replace />;
      }
    }

    const message =
      activeError ??
      (user?.is_superadmin === true
        ? `Workspace "${tenantSlug}" was not found.`
        : `Workspace "${tenantSlug}" was not found or you do not have access.`);

    return <TenantErrorView message={message} />;
  }

  const queryScope = tenantQueryScope(context.tenantId, user);
  return (
    <TenantCtx value={context}>
      <TenantQueryProvider key={queryScope.key} demoMode={queryScope.demoMode}>
        <PageAccessProvider>
          <QuotaBlockProvider>
            <Outlet />
          </QuotaBlockProvider>
        </PageAccessProvider>
      </TenantQueryProvider>
    </TenantCtx>
  );
}
